import { gql, useMutation, useQuery } from '@apollo/client'
import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  Button,
  IconButton,
  Input,
  Text,
  FormErrorMessage,
  useColorModeValue,
} from '@chakra-ui/react'
import { useFormik } from 'formik'
import { useState, Fragment } from 'react'
import { FaTrash } from 'react-icons/fa'
import { useParams } from 'react-router-dom'

const ENVIRONMENT_QUERY = gql`
  query Environment($appName: String!) {
    app(name: $appName) {
      id
      envVars {
        id
        key
        value
      }
    }
  }
`

const CREATE_ENVIRONMENT_VARIABLE_MUTATION = gql`
  mutation ($appName: String!, $key: String!, $value: String!) {
    createEnvVar(appName: $appName, key: $key, value: $value) {
      id
    }
  }
`

const DELETE_ENVIRONMENT_VARIABLE_MUTATION = gql`
  mutation ($idEnvVar: String!) {
    deleteEnvVar(id: $idEnvVar)
  }
`

const initialFormValues = {
  key: '',
  value: '',
}

const Environment = () => {
  const borderColor = useColorModeValue('gray.300', 'whiteAlpha.300')
  const { name } = useParams()

  const [isDeletingEnvVar, setIsDeletingEnvVar] = useState(false)

  const {
    data,
    // loading,
    // error,
    refetch: refetchEnvVarList,
  } = useQuery(ENVIRONMENT_QUERY, {
    variables: { appName: name },
    pollInterval: 5000,
  })

  const [createEnvVar] = useMutation(CREATE_ENVIRONMENT_VARIABLE_MUTATION)
  const [deleteEnvVar] = useMutation(DELETE_ENVIRONMENT_VARIABLE_MUTATION)

  const formik = useFormik({
    initialValues: initialFormValues,
    validate: (values) => {
      const errors: Record<string, string> = {}

      if (!values.key) {
        errors.key = 'Required'
      }

      if (!values.value) {
        errors.value = 'Required'
      }

      return errors
    },
    onSubmit: async (values) => {
      const variables = {
        ...values,
        appName: name,
        key: values.key.trim(),
      }

      await createEnvVar({ variables })
      await refetchEnvVarList()
      formik.resetForm()
    },
  })

  const isControlInvalid = (controlName: keyof typeof initialFormValues) =>
    !!(formik.errors[controlName] && formik.touched[controlName])

  const app = data?.app ?? {}

  return (
    <Box p={4} rounded="lg" border="1px" borderColor={borderColor}>
      <Heading as="h2" size="md">
        Environment Variables
      </Heading>
      <Text mt={4}>
        In order to store API keys, other configuration values or secrets, use
        environment variables.
      </Text>
      <form style={{ marginTop: '1rem' }} onSubmit={formik.handleSubmit}>
        <Grid gridTemplateColumns="1fr 2fr 120px" gap={8}>
          <FormControl
            colorScheme="cyan"
            isRequired
            isInvalid={isControlInvalid('key')}
            position="relative"
          >
            <FormLabel htmlFor="key">Key</FormLabel>
            <Input
              type="text"
              id="key"
              name="key"
              onChange={formik.handleChange}
              value={formik.values.key}
              isDisabled={formik.isSubmitting}
            />
            <FormErrorMessage position="absolute" bottom={-6}>{formik.errors.key}</FormErrorMessage>
          </FormControl>
          <FormControl
            colorScheme="cyan"
            isRequired
            isInvalid={isControlInvalid('value')}
            position="relative"
          >
            <FormLabel htmlFor="value">Value</FormLabel>
            <Input
              type="text"
              id="value"
              name="value"
              onChange={formik.handleChange}
              value={formik.values.value}
              isDisabled={formik.isSubmitting}
            />
            <FormErrorMessage position="absolute" bottom={-6}>{formik.errors.value}</FormErrorMessage>
          </FormControl>
          <Button
            type="submit"
            alignSelf="end"
            colorScheme="cyan"
            color="white"
            variant="solid"
            isDisabled={formik.isSubmitting}
            onClick={() => formik.handleSubmit()}
          >
            Add
          </Button>
          {app?.envVars?.map((envVar: any) => (
            <Fragment key={envVar.key}>
              <Input type="text" value={envVar.key} isDisabled />
              <Input type="password" value={envVar.value} isDisabled />
              <IconButton
                aria-label="Delete key-value pair"
                onClick={async () => {
                  try {
                    setIsDeletingEnvVar(true)
                    await deleteEnvVar({ variables: { idEnvVar: envVar.id } })
                    await refetchEnvVarList()
                  } finally {
                    setIsDeletingEnvVar(false)
                  }
                }}
                icon={<FaTrash />}
                isDisabled={isDeletingEnvVar}
              />
            </Fragment>
          ))}
        </Grid>
      </form>
    </Box>
  )
}

export default Environment
